import { useState } from 'react'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { useEmployees } from '../../hooks/useEmployees'
import { useRoles } from '../../hooks/useRoles'
import { useDepartments } from '../../hooks/useDepartments'
import { Input } from '../ui/input'
import { LoadingSpinner } from '../ui/loading-states'

export type RecipientType = 'employee' | 'role' | 'department' | 'broadcast'

interface RecipientSelectorProps {
  recipientType: RecipientType
  recipientId: string
  onRecipientTypeChange: (type: RecipientType) => void
  onRecipientIdChange: (id: string) => void
  disabled?: boolean
}

export function RecipientSelector({
  recipientType,
  recipientId,
  onRecipientTypeChange,
  onRecipientIdChange,
  disabled = false,
}: RecipientSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch data based on recipient type
  // 1000 — потолок страницы на бэкенде (PaginationQueryDto.MAX_PAGE_SIZE):
  // больше — 400, и список пуст при любом поиске. Остальных находит поиск.
  const employeesQuery = useEmployees({ search: searchQuery, limit: 1000, isActive: true })
  const rolesQuery = useRoles({})
  const departmentsQuery = useDepartments({})

  const handleTypeChange = (value: string) => {
    onRecipientTypeChange(value as RecipientType)
    onRecipientIdChange('') // Reset recipient ID when type changes
  }

  return (
    <div className="space-y-4">
      {/* Recipient Type Selection */}
      <div className="space-y-2">
        <Label htmlFor="recipientType">Тип получателя</Label>
        <Select value={recipientType} onValueChange={handleTypeChange} disabled={disabled}>
          <SelectTrigger id="recipientType">
            <SelectValue placeholder="Выберите тип получателя" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="employee">Сотрудник</SelectItem>
            <SelectItem value="role">Роль</SelectItem>
            <SelectItem value="department">Отдел</SelectItem>
            <SelectItem value="broadcast">Всем сотрудникам</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {recipientType === 'employee' && 'Уведомление будет отправлено выбранному сотруднику'}
          {recipientType === 'role' && 'Уведомление будет отправлено всем пользователям с выбранной ролью'}
          {recipientType === 'department' && 'Уведомление будет отправлено всем сотрудникам отдела'}
          {recipientType === 'broadcast' && 'Уведомление будет отправлено всем сотрудникам компании'}
        </p>
      </div>

      {/* Recipient Selection (for non-broadcast types) */}
      {recipientType !== 'broadcast' && (
        <div className="space-y-2">
          <Label htmlFor="recipient">
            {recipientType === 'employee' && 'Выберите сотрудника'}
            {recipientType === 'role' && 'Выберите роль'}
            {recipientType === 'department' && 'Выберите отдел'}
          </Label>

          {/* Employee Search Input */}
          {recipientType === 'employee' && (
            <Input
              type="text"
              placeholder="Поиск сотрудника..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={disabled}
              className="mb-2"
            />
          )}

          {/* Employee Selector */}
          {recipientType === 'employee' && (
            <>
              {employeesQuery.isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <>
                  <Select value={recipientId} onValueChange={onRecipientIdChange} disabled={disabled}>
                    <SelectTrigger id="recipient">
                      <SelectValue placeholder="Выберите сотрудника" />
                    </SelectTrigger>
                    <SelectContent>
                      {employeesQuery.data?.data.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          {employee.first_name} {employee.last_name}
                          {employee.department && ` - ${employee.department.name}`}
                        </SelectItem>
                      ))}
                      {employeesQuery.data?.data.length === 0 && (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          Сотрудники не найдены
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  {employeesQuery.data?.data && employeesQuery.data.data.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Найдено сотрудников: {employeesQuery.data.data.length}
                      {employeesQuery.data.total > employeesQuery.data.data.length &&
                        ` из ${employeesQuery.data.total} (используйте поиск для уточнения)`}
                    </p>
                  )}
                </>
              )}
            </>
          )}

          {/* Role Selector */}
          {recipientType === 'role' && (
            <>
              {rolesQuery.isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <Select value={recipientId} onValueChange={onRecipientIdChange} disabled={disabled}>
                  <SelectTrigger id="recipient">
                    <SelectValue placeholder="Выберите роль" />
                  </SelectTrigger>
                  <SelectContent>
                    {rolesQuery.data?.data.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                    {rolesQuery.data?.data.length === 0 && (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        Роли не найдены
                      </div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </>
          )}

          {/* Department Selector */}
          {recipientType === 'department' && (
            <>
              {departmentsQuery.isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <Select value={recipientId} onValueChange={onRecipientIdChange} disabled={disabled}>
                  <SelectTrigger id="recipient">
                    <SelectValue placeholder="Выберите отдел" />
                  </SelectTrigger>
                  <SelectContent>
                    {departmentsQuery.data?.data.map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))}
                    {departmentsQuery.data?.data.length === 0 && (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        Отделы не найдены
                      </div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </>
          )}

          {!recipientId && (
            <p className="text-xs text-destructive">Выберите получателя</p>
          )}
        </div>
      )}

      {/* Broadcast confirmation */}
      {recipientType === 'broadcast' && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
          <p className="text-sm text-orange-800 font-medium">
            ⚠️ Уведомление будет отправлено всем сотрудникам
          </p>
          <p className="text-xs text-orange-600 mt-1">
            Убедитесь, что содержимое подходит для массовой рассылки
          </p>
        </div>
      )}
    </div>
  )
}
